import Box from "@mui/material/Box";
import { styled, Theme, useTheme } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import EntityMembersTable from "_shared/entity-members/_views/entity-members-table";
import Guid from "_shared/guids/guid";
import { PageTab } from "legal-entities/administrators/_views/administration-page-tabs";
import AdministratorInvitationsTable from "legal-entities/invitations/administrator-invitations/_views/tables/administrator-invitations-table";
import EntityMemberInvitationForm from "legal-entities/invitations/member-invitations/_views/entity-member-invitation-form";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  MRT_ShowHideColumnsButton as ToggleShowHideColumnsButton,
} from "material-react-table";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import PermissionsTable from "permissions/_views/tables/permissions-table";
import { JSX, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

export default function EntityAdministration() {
  const routeParams = useLoaderData();
  const activeTab = routeParams?.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Users;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const theme = useTheme();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const isInviteUsersDialogOpen = useRef(false);
  const isViewProfileDialogOpen = useRef(false);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const handleViewProfile = useCallback(
    (userId?: Guid): void => {
      if (!userId || isViewProfileDialogOpen.current) return;
      isViewProfileDialogOpen.current = true;
      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: <ViewIndividualProfile userId={userId} />,
        onClose: () => {
          isViewProfileDialogOpen.current = false;
          navigate(parentPath.pathname);
        },
      });
    },
    [navigate, openDialog],
  );

  const handleInviteUsers = useCallback(() => {
    if(isInviteUsersDialogOpen.current) return;
    isInviteUsersDialogOpen.current = true;
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);
    openDialog({
      title: "User Invitations",
      resetStack: true,
      MuiProps: {
        maxWidth: "md",
      },
      component: <EntityMemberInvitationForm confirmDialog={confirm} />,
      onClose: () => {
        isInviteUsersDialogOpen.current = false;
        navigate(parentPath.pathname);
      },
    });
  }, [confirm, navigate, openDialog]);

  useEffect(() => {
    // Handle URL changes
    if (!routeParams.pageTab) {
      navigate(`/administration/${PageTab.Users}`);
    } else if (routeParams.action === "invite") {
      handleInviteUsers();
    } else if (routeParams.action === "view" && routeParams.id) {
      handleViewProfile(new Guid(routeParams.id));
    }
  }, [handleInviteUsers, handleViewProfile, navigate, routeParams.pageTab, routeParams.action, routeParams.id]);

  const renderTopToolbarCustomActions = <T extends RowData>(
    props: { table: TableInstance<T> },
    additional?: JSX.Element | undefined,
    filters?: JSX.Element | undefined,
  ): JSX.Element => {
    const { table } = props;

    const params = new URLSearchParams(location.search);
    const isManagingTemplates = params.get("action") === "manage-templates";

    return (
      <TableTopToolbarContainer>
        <TableTitle variant="h4">Administration</TableTitle>
        <LowerTopToolbarContainer>
          <Tabs
            value={routeParams.pageTab ?? PageTab.Users}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_event, newValue) => navigate(`/administration/${newValue}`)}
          >
            <Tab value={PageTab.Users} label="Users" />
            <Tab value={PageTab.Invitations} label="Invitations" />
            <Tab
              value={PageTab.Permissions}
              label={
                isManagingTemplates ? (
                  <>
                    <span style={{ color: theme.palette.text.secondary }}>Permissions</span>&#128898; Templates
                  </>
                ) : (
                  "Permissions"
                )
              }
            />
          </Tabs>
          {additional}
        </LowerTopToolbarContainer>
        {filters}
        {isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ToggleShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    );
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToAdministration) return <NotAuthorized />;

  const entityMembersTable = <EntityMembersTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;

  switch (activeTab) {
    case PageTab.Users:
      return entityMembersTable;
    case PageTab.Invitations:
      return <AdministratorInvitationsTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;
    case PageTab.Permissions:
      return <PermissionsTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} />;
    default:
      return entityMembersTable;
  }
}
